import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import type { Group, GroupWrite } from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../../../app/providers/FirebaseProvider';
import { updateGroupSettings, getGroupById } from '../api/groupRepository';

// Group settings validation schema
export const groupSettingsSchema = z.object({
  name: z.string().min(1).max(80),
  summary: z.string().max(280).optional(),
  autoAcceptMembers: z.boolean(),
  organizerUid: z.string().min(1),
  organizerName: z.string().min(1),
  tags: z.array(z.string().min(1).max(30)).max(10).optional(),
  contactInfo: z
    .object({
      email: z.string().email().optional(),
      website: z.string().url().optional(),
      discord: z.string().optional(),
      slack: z.string().optional(),
    })
    .optional(),
});

export type GroupSettingsData = z.infer<typeof groupSettingsSchema>;

export type GroupSettingsState = {
  readonly group: Group | null;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly error: Error | null;
  readonly validationErrors: Record<string, string[]> | null;
  readonly updateSettings: (settings: Partial<GroupSettingsData>) => Promise<void>;
  readonly resetForm: () => void;
  readonly hasUnsavedChanges: boolean;
};

export const useGroupSettings = (
  groupId: string | null,
  organizerUid: string | null,
): GroupSettingsState => {
  const { firestore } = useFirebaseServices();
  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(groupId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<
    string,
    string[]
  > | null>(null);
  const [originalSettings, setOriginalSettings] = useState<GroupSettingsData | null>(
    null,
  );
  const [currentSettings, setCurrentSettings] = useState<GroupSettingsData | null>(null);

  // Check if current user is the organizer
  const isOrganizer = group?.organizerUid === organizerUid;

  // Check for unsaved changes
  const hasUnsavedChanges =
    currentSettings !== null &&
    originalSettings !== null &&
    JSON.stringify(currentSettings) !== JSON.stringify(originalSettings);

  const validateSettings = useCallback(
    (settings: Partial<GroupSettingsData>): Record<string, string[]> | null => {
      try {
        groupSettingsSchema.parse({
          ...currentSettings,
          ...settings,
        });
        return null;
      } catch (validationError) {
        if (validationError instanceof z.ZodError) {
          const errorsByField: Record<string, string[]> = {};
          validationError.errors.forEach(error => {
            const path = error.path.join('.');
            if (!errorsByField[path]) {
              errorsByField[path] = [];
            }
            errorsByField[path].push(error.message);
          });
          return errorsByField;
        }
        return { general: ['Validation failed'] };
      }
    },
    [currentSettings],
  );

  const updateSettings = useCallback(
    async (settingsUpdate: Partial<GroupSettingsData>): Promise<void> => {
      if (!groupId || !isOrganizer || saving) {
        return;
      }

      // Validate the merged settings
      const mergedSettings = { ...currentSettings, ...settingsUpdate };
      const errors = validateSettings(settingsUpdate);

      if (errors) {
        setValidationErrors(errors);
        return;
      }

      setSaving(true);
      setError(null);
      setValidationErrors(null);

      try {
        // Optimistic update
        setCurrentSettings(mergedSettings as GroupSettingsData);

        if (group) {
          const updatedGroup: Group = {
            ...group,
            name: mergedSettings.name || group.name,
            summary: mergedSettings.summary,
            autoAcceptMembers:
              mergedSettings.autoAcceptMembers ?? group.autoAcceptMembers,
            organizerUid: mergedSettings.organizerUid || group.organizerUid,
            organizerName: mergedSettings.organizerName || group.organizerName,
          };
          setGroup(updatedGroup);
        }

        // Prepare the update payload for Firestore
        const updatePayload: Partial<GroupWrite> = {
          name: mergedSettings.name,
          summary: mergedSettings.summary,
          autoAcceptMembers: mergedSettings.autoAcceptMembers,
          organizerUid: mergedSettings.organizerUid,
          organizerName: mergedSettings.organizerName,
        };

        await updateGroupSettings(firestore, groupId, updatePayload);

        // Update original settings to reflect saved state
        setOriginalSettings(mergedSettings as GroupSettingsData);
      } catch (updateError) {
        // Revert optimistic update
        setCurrentSettings(currentSettings);
        if (group && originalSettings) {
          const revertedGroup: Group = {
            ...group,
            name: originalSettings.name,
            summary: originalSettings.summary,
            autoAcceptMembers: originalSettings.autoAcceptMembers,
            organizerUid: originalSettings.organizerUid,
            organizerName: originalSettings.organizerName,
          };
          setGroup(revertedGroup);
        }

        console.error('Failed to update group settings:', updateError);
        setError(
          updateError instanceof Error
            ? updateError
            : new Error('Failed to update group settings'),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      firestore,
      groupId,
      isOrganizer,
      saving,
      currentSettings,
      validateSettings,
      group,
      originalSettings,
    ],
  );

  const resetForm = useCallback(() => {
    if (originalSettings) {
      setCurrentSettings(originalSettings);
      setValidationErrors(null);
      setError(null);
    }
  }, [originalSettings]);

  // Load group data
  useEffect(() => {
    if (!groupId) {
      setGroup(null);
      setLoading(false);
      setOriginalSettings(null);
      setCurrentSettings(null);
      return;
    }

    setLoading(true);
    setError(null);

    const loadGroup = async (): Promise<void> => {
      try {
        const fetchedGroup = await getGroupById(firestore, groupId);
        setGroup(fetchedGroup);

        if (fetchedGroup) {
          const settings: GroupSettingsData = {
            name: fetchedGroup.name,
            summary: fetchedGroup.summary,
            autoAcceptMembers: fetchedGroup.autoAcceptMembers,
            organizerUid: fetchedGroup.organizerUid,
            organizerName: fetchedGroup.organizerName,
            tags: undefined, // TODO: Add tags to Group type
            contactInfo: undefined, // TODO: Add contact info to Group type
          };

          setOriginalSettings(settings);
          setCurrentSettings(settings);
        }
      } catch (fetchError) {
        console.error('Failed to load group:', fetchError);
        setError(
          fetchError instanceof Error ? fetchError : new Error('Failed to load group'),
        );
      } finally {
        setLoading(false);
      }
    };

    void loadGroup();
  }, [firestore, groupId]);

  return {
    group,
    loading,
    saving,
    error,
    validationErrors,
    updateSettings,
    resetForm,
    hasUnsavedChanges,
  };
};
